import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom'; // Removed useNavigate
import { supabase } from '../lib/db.ts';
import { useAuth } from '../lib/auth.tsx';
import { Mail, Send, AlertCircle } from 'lucide-react';
import { logger } from '../services/logger.ts';

export function ContactSupportPage() {
  // const navigate = useNavigate(); // Keep commented out or remove if not planned soon
  const { user } = useAuth();

  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!email.trim() || !subject.trim() || !message.trim()) {
      setError('Please fill in all fields.');
      return;
    }

    setLoading(true);

    try {
      // Call the RPC function
      const { data: newRequest, error: rpcError } = await supabase.rpc(
        'submit_customer_service_request',
        {
          subject_in: subject.trim(),
          message_in: message.trim(),
          email_from_form: email.trim()
        }
      );

      if (rpcError) {
        throw rpcError;
      }

      // If using the returned data, ensure it matches expected structure
      logger.info('Successfully submitted request via RPC:', newRequest);

      setSuccess(true);
      setSubject('');
      setMessage('');
      // Email might be cleared or kept depending on if it's pre-filled for logged-in user
      // If user is not logged in, clear email: if (!user) setEmail('');
    } catch (err: unknown) {
      logger.error('Error submitting customer service request via RPC:', err);
      if (err instanceof Error) {
        setError(err.message || 'An error occurred. Please try again.');
      } else if (typeof err === 'object' && err !== null && 'message' in err) {
        // Handle Supabase RPCError like objects
        setError((err as { message: string }).message || 'An RPC error occurred. Please try again.');
      } else {
        setError('An unknown error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Contact Support
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Have a question or need help? Fill out the form below.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-xl">
        <div className="bg-white py-8 px-4 shadow-xl sm:rounded-lg sm:px-10">
          {success && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
              <p>Your request has been submitted successfully! We will get back to you soon.</p>
            </div>
          )}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md flex items-start">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" aria-hidden="true" />
              <p>{error}</p>
            </div>
          )}
          {!success && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={!!user?.email || loading} // Disable if pre-filled from logged-in user
                    className="focus:ring-wayja-teal focus:border-wayja-teal block w-full pl-10 sm:text-sm border-gray-300 rounded-md py-2"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                  Subject
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="subject"
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="shadow-sm focus:ring-wayja-teal focus:border-wayja-teal block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3"
                    placeholder="e.g., Issue with my account"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                  Message
                </label>
                <div className="mt-1">
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="shadow-sm focus:ring-wayja-teal focus:border-wayja-teal block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3"
                    placeholder="Please describe your issue or question in detail..."
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-wayja-teal hover:bg-wayja-teal-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-wayja-teal disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2 -ml-1" />
                      Send Request
                    </>
                  )
                  }
                </button>
              </div>
            </form>
          )}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or</span>
              </div>
            </div>
            <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                    If you prefer, you can also email us directly at <a href="mailto:support@wayja.me" className="font-medium text-wayja-teal hover:text-wayja-teal-dark">support@wayja.me</a>.
                </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 